import { Dictionary } from "@/lib/Dictionary.type";
import ApiCollectionResponseParserService from "@/modules/api/api-collection-response-parser.service";
import ApiService from "@/modules/api/api.service";
import BasicRepository from "@/modules/api/basic.repository";
import ObjectBuilderInterface from "@/modules/api/object-builder.interface";
import { ResourceCollection } from "@/modules/api/resource.collection";

import FactoryPhaseSupport from "./factory-phase-support.model";

export default class FactoryPhaseSupportRepository extends BasicRepository<FactoryPhaseSupport> {
  constructor(
    api: ApiService,
    objectBuilder: ObjectBuilderInterface<FactoryPhaseSupport>,
    responseParser: ApiCollectionResponseParserService<FactoryPhaseSupport>
  ) {
    super("factory_phase_supports", api, objectBuilder, responseParser);
  }

  public getByFactoryId(
    factoryId: string,
    useCache = true
  ): Promise<ResourceCollection<FactoryPhaseSupport>> {
    return this.getList(1, 999, { factory: factoryId }, undefined, useCache);
  }

  public async getCompleteByFactoryIds(
    factoryIds: string[]
  ): Promise<FactoryPhaseSupport[]> {
    const ids = Array.from(new Set(factoryIds));
    const result: FactoryPhaseSupport[] = [];

    for (let offset = 0; offset < ids.length; offset += ApiService.MAX_GUIDS_PER_GET_REQUEST) {
      const factories = ids.slice(offset, offset + ApiService.MAX_GUIDS_PER_GET_REQUEST);
      const items: FactoryPhaseSupport[] = [];
      let total = -1;
      let pages = -1;
      let page = 1;

      do {
        const collection = await this.getList(
          page,
          BasicRepository.MAX_ITEMS_PER_PAGE,
          { factory: factories },
          undefined,
          false
        );
        if (
          !Number.isInteger(collection.totalItemsCount) ||
          collection.totalItemsCount < 0 ||
          !Number.isInteger(collection.totalPages) ||
          collection.totalPages < 1 ||
          collection.page !== page ||
          (total !== -1 && total !== collection.totalItemsCount) ||
          (pages !== -1 && pages !== collection.totalPages)
        ) {
          throw new Error("Incomplete factory phase support collection");
        }
        total = collection.totalItemsCount;
        pages = collection.totalPages;
        items.push(...collection.getItems());
        page += 1;
      } while (page <= pages);

      if (items.length !== total || new Set(items.map((item) => item.id)).size !== total ||
          items.some((item) => !factories.includes(item.factory))) {
        throw new Error("Incomplete factory phase support collection");
      }
      result.push(...items);
    }

    return result;
  }

  public async create(item: FactoryPhaseSupport): Promise<FactoryPhaseSupport> {
    const response = await this.api.post(
      this.resourceName,
      this.prepareData(item)
    );

    return this.objectBuilder.buildFromJSON(response.data);
  }

  protected prepareData(
    item: FactoryPhaseSupport
  ): Dictionary<string | number | boolean | string[]> {
    return {
      id: item.id,
      factory: item.factory,
      slotType: item.slotType,
    };
  }
}
